import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { projects, paymentPlan } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MapPin, ArrowUpRight, BedDouble, Bath, Ruler, Share2, Check, Dot } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { CtaBanner } from '../components/site/BottomSections';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function ProjectDetail() {
  const { id } = useParams();
  const project = projects.find((p) => p.id === id);
  const [active, setActive] = useState(0);
  const [brochure, setBrochure] = useState({ name: '', phone: '', email: '' });
  const [busy, setBusy] = useState(false);

  if (!project) return <Navigate to="/projects" replace />;

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: project.name, text: project.tagline, url }); } catch (_) {}
    } else {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied', description: 'Share it with anyone.' });
    }
  };

  const requestBrochure = async (e) => {
    e.preventDefault();
    if (!brochure.name || !brochure.phone) {
      toast({ title: 'Add your name and phone', description: 'We need both to send the brochure.' });
      return;
    }
    try {
      setBusy(true);
      await axios.post(`${API}/brochure-requests`, { ...brochure, project_id: project.id });
      toast({ title: 'Brochure on its way', description: `We\u2019ll email the ${project.name} brochure within 15 minutes.` });
      setBrochure({ name: '', phone: '', email: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[80vh] min-h-[560px] overflow-hidden bg-[#0f1a14]">
        <img src={project.gallery[active]} alt={project.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1a14]/60 via-transparent to-[#0f1a14]/95" />
        <div className="relative mx-auto max-w-7xl h-full px-6 lg:px-10 flex flex-col justify-end pb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#c9a56a] text-[#0f1a14] px-3 py-1 text-[10px] uppercase tracking-widest">{project.tag}</span>
            <span className="flex items-center gap-1 text-[#c9a56a] text-xs uppercase tracking-widest">
              <MapPin className="h-3 w-3" /> {project.location}
            </span>
          </div>
          <h1 className="font-serif text-white text-5xl md:text-7xl leading-[1.05] max-w-4xl">{project.name}</h1>
          <p className="text-white/75 mt-5 max-w-xl text-lg">{project.tagline}</p>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <div className="font-serif text-3xl text-white">{project.price}</div>
            <button onClick={share} className="inline-flex items-center gap-2 text-white/80 hover:text-[#c9a56a] text-xs uppercase tracking-widest border border-white/30 hover:border-[#c9a56a] px-4 py-2">
              <Share2 className="h-3.5 w-3.5" /> Share
            </button>
          </div>
        </div>
      </section>

      {/* Gallery thumbnails */}
      <section className="bg-[#0f1a14] pt-6">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex gap-3 overflow-x-auto pb-4">
            {project.gallery.map((g, i) => (
              <button key={i} onClick={() => setActive(i)} className={`shrink-0 relative w-32 h-20 overflow-hidden border-2 transition-all ${active === i ? 'border-[#c9a56a]' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                <img src={g} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Highlights + description */}
      <section className="bg-[#f6f1e7] py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-3 gap-14">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#8a6b3a]" />
              <span className="text-[#8a6b3a] text-xs uppercase tracking-[0.3em]">Overview</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0f1a14] leading-[1.1]">The place, at a glance.</h2>
            <p className="text-[#0f1a14]/70 leading-relaxed text-lg mt-6">{project.description}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#0f1a14]/10 border border-[#0f1a14]/10 mt-10">
              {project.highlights.map((h) => (
                <div key={h.label} className="bg-white p-6">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#8a6b3a]">{h.label}</div>
                  <div className="font-serif text-2xl text-[#0f1a14] mt-2">{h.value}</div>
                </div>
              ))}
            </div>
            <h3 className="font-serif text-2xl text-[#0f1a14] mt-14 mb-5">Amenities</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {project.amenities.map((a) => (
                <div key={a} className="flex items-center gap-2 text-[#0f1a14]/80 text-sm">
                  <Check className="h-4 w-4 text-[#8a6b3a]" /> {a}
                </div>
              ))}
            </div>
          </div>

          {/* Brochure form */}
          <form onSubmit={requestBrochure} className="bg-white p-8 border border-[#0f1a14]/10 h-fit sticky top-24">
            <div className="text-xs uppercase tracking-widest text-[#8a6b3a]">Get the brochure</div>
            <h3 className="font-serif text-2xl text-[#0f1a14] mt-2">Floor plans, prices & payment plan.</h3>
            <div className="space-y-3 mt-6">
              <Input placeholder="Full name" className="rounded-none h-12 border-[#0f1a14]/15" value={brochure.name} onChange={(e) => setBrochure({ ...brochure, name: e.target.value })} />
              <Input placeholder="Phone number" className="rounded-none h-12 border-[#0f1a14]/15" value={brochure.phone} onChange={(e) => setBrochure({ ...brochure, phone: e.target.value })} />
              <Input placeholder="Email (optional)" type="email" className="rounded-none h-12 border-[#0f1a14]/15" value={brochure.email} onChange={(e) => setBrochure({ ...brochure, email: e.target.value })} />
            </div>
            <Button type="submit" disabled={busy} className="w-full mt-5 bg-[#0f1a14] text-white hover:bg-[#1c2e26] rounded-none py-6 tracking-wider">
              {busy ? 'Sending...' : 'Send Me the Brochure'}
            </Button>
            <p className="text-xs text-[#0f1a14]/50 mt-3">Or call <a href="tel:+918526164444" className="text-[#8a6b3a] hover:text-[#0f1a14]">+91 852-616-4444</a></p>
          </form>
        </div>
      </section>

      {/* Floor plans */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#8a6b3a]" />
            <span className="text-[#8a6b3a] text-xs uppercase tracking-[0.3em]">Floor Plans</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#0f1a14] leading-[1.1]">Homes for every kind of story.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {project.floorPlans.map((fp) => (
              <div key={fp.name} className="group border border-[#0f1a14]/10 bg-[#f6f1e7]/40 p-8 hover:border-[#8a6b3a] transition-colors">
                <FloorPlanSVG />
                <h3 className="font-serif text-xl text-[#0f1a14] mt-6">{fp.name}</h3>
                <div className="flex gap-4 text-xs text-[#0f1a14]/60 mt-3">
                  <span className="flex items-center gap-1"><BedDouble className="h-3.5 w-3.5 text-[#8a6b3a]" /> {fp.beds} Bed</span>
                  <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5 text-[#8a6b3a]" /> {fp.baths} Bath</span>
                  <span className="flex items-center gap-1"><Ruler className="h-3.5 w-3.5 text-[#8a6b3a]" /> {fp.area}</span>
                </div>
                <div className="mt-5 flex items-center justify-between">
                  <div className="font-serif text-2xl text-[#0f1a14]">{fp.price}</div>
                  <Link to="/contact" className="inline-flex items-center gap-1 text-[#8a6b3a] hover:text-[#0f1a14] text-xs uppercase tracking-widest">
                    Enquire <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Price table / payment plan */}
      <section className="bg-[#0f1a14] text-white py-24">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#c9a56a]" />
            <span className="text-[#c9a56a] text-xs uppercase tracking-[0.3em]">Cost & Payment Plan</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.1]">Simple, transparent, buyer‑first.</h2>
          <div className="mt-10 divide-y divide-white/10 border-y border-white/10">
            {paymentPlan.map((p) => (
              <div key={p.step} className="py-5 grid grid-cols-12 gap-4 items-center hover:bg-white/5 transition-colors px-3">
                <div className="col-span-1 font-serif text-2xl text-[#c9a56a]">{p.step}</div>
                <div className="col-span-6 md:col-span-5">
                  <div className="font-medium">{p.title}</div>
                  <div className="text-xs text-white/50">{p.note}</div>
                </div>
                <div className="col-span-5 md:col-span-6 text-right font-serif text-2xl">{p.percent}</div>
              </div>
            ))}
          </div>
          <p className="text-white/50 text-sm mt-6 flex items-center gap-2"><Dot className="h-4 w-4" /> Construction cost varies with design and requirements of your choice.</p>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}

function FloorPlanSVG() {
  return (
    <svg viewBox="0 0 240 140" className="w-full h-32" xmlns="http://www.w3.org/2000/svg">
      <rect x="4" y="4" width="232" height="132" fill="none" stroke="#8a6b3a" strokeWidth="2" />
      <line x1="100" y1="4" x2="100" y2="90" stroke="#8a6b3a" strokeWidth="1" />
      <line x1="4" y1="90" x2="236" y2="90" stroke="#8a6b3a" strokeWidth="1" />
      <line x1="160" y1="90" x2="160" y2="136" stroke="#8a6b3a" strokeWidth="1" />
      <rect x="20" y="20" width="20" height="12" fill="#c9a56a" opacity="0.4" />
      <rect x="120" y="20" width="36" height="12" fill="#c9a56a" opacity="0.4" />
      <text x="50" y="58" fill="#8a6b3a" fontSize="9" fontFamily="serif">Living</text>
      <text x="140" y="58" fill="#8a6b3a" fontSize="9" fontFamily="serif">Bedroom</text>
      <text x="30" y="118" fill="#8a6b3a" fontSize="9" fontFamily="serif">Kitchen</text>
      <text x="180" y="118" fill="#8a6b3a" fontSize="9" fontFamily="serif">Deck</text>
    </svg>
  );
}
